import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ErrorFallback, Loader } from 'smart-react';
import { Card } from '@progress/kendo-react-layout';
import '@fortawesome/fontawesome-free/css/fontawesome.css';
import '@fortawesome/fontawesome-free/css/solid.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWandMagicSparkles,
  faPaperPlane,
  faPlus,
  faPaperclip,
  faTimes,
  faBolt,
  faTh
} from '@fortawesome/free-solid-svg-icons';
import { ListView } from '@progress/kendo-react-listview';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { buildNotification, GenerateNotification } from 'smart-react';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES
} from '../../../constants/eventDataTypes';
import {
  listAssistants,
  getConfigurationByType
} from '../../Assistant/Services/AiAssistantService';
import {
  createThread,
  sendMessage,
  createRun,
  listMessages,
  retrieveRun,
  handleOpenAIStream,
  improvePrompt
} from '../Services/OpenAIChatService';
import {
  listChatSessions,
  createChatSession,
  updateChatSession,
  deleteChatSession,
  fetchChatSession
} from '../Services/ChatSessionService';
import { deleteFile, uploadFile } from '../../Assistant/Services/FileService';
import { sendBYMessage, BYLogin } from '../Services/BlueYonderChatService';
import {
  sendQuestion,
  handleSmartRagStream
} from '../Services/SmartRagChatService';
import {
  generateSessionKeyFunction,
  formatSessionDate
} from '../../../Utils/Common/generateSessionKey';
import { ErrorBoundary } from 'react-error-boundary';
import './SmartChat.scss';
import SmartChatDashboard from './SmartChatDashboard';
import {
  ASSISTANT_TYPES,
  SPECIAL_ASSISTANT_ADMIN_TYPES
} from '../../../constants/applicationConstants';
import { createFormData } from '../../../Utils/Api/Api';
import AssistantSelect from '../../SmartBot/Components/AssistantDropdown';
import ChatMessage from '../../Core/Components/Common/ChatMessage';
import AddToDashboardModal from '../Components/AddToDashboardModal';
import MentionDropdown from '../Components/MentionDropdown';
import '../Components/MentionDropdown.scss';
import {
  getCursorPosition,
  findMentionStart,
  getMentionSearchTerm,
  insertMention,
  parseMentions
} from '../../../Utils/Common/mentionUtils';

// Helper: strip assistant @mentions (e.g. @SalesBot) from a prompt while
// leaving other uses of '@' intact (such as email addresses).
// We only remove tokens that start at the beginning of the string or
// immediately after whitespace:
//   "@Bot show data"      -> "show data"
//   "run with @Bot now"  -> "run with  now"
//   "testing@domain"  -> stays unchanged
const stripAssistantMentions = (text = '') =>
  text.replace(/(^|\s)@\w+/g, '$1');

/**
 * Component for managing chat with AI assistants.
 * @param {Object} props - Component props.
 * @param {string} props.assistantId - The ID of the assistant.
 * @param {string} props.assistantName - The name of the assistant.
 * @returns {JSX.Element} SmartChat component.
 */
const SmartChat = ({ assistantId, assistantName }) => {
  const [isStreamingResponse, setIsStreamingResponse] = useState(true);
  const [isLoader, setIsLoader] = useState(false);
  const [sending, setSending] = useState(false);
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const chatWindowRef = useRef(null);
  const textareaRef = useRef(null);
  const mainChatAreaRef = useRef(null);
  const [showPromptOverlay, setShowPromptOverlay] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [sessionKey, setSessionKey] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentDetails, setAttachmentDetails] = useState({});
  const [BYLoginSession, setBYLoginSession] = useState(false);
  const [assistantError, setAssistantError] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isStreamingInProgress, setIsStreamingInProgress] = useState(false);
  const [assistantSessions, setAssistantSessions] = useState({});
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [showAddToDashboard, setShowAddToDashboard] = useState(false);
  const [selectedTileMessage, setSelectedTileMessage] = useState(null);
  const [showDashboardOverlay, setShowDashboardOverlay] = useState(false);
  // Bump this to force SmartChatDashboard to re-fetch tiles (useful when modal adds a tile)
  const [dashboardRefreshToken, setDashboardRefreshToken] = useState(0);
  // Unique SmartApps AppKeys (sa_tenant_key) for BY/SMARTAPPS/ENTERPRISE assistants
  // Used by SmartChatDashboard so it doesn't need to call the assistants API again.
  const [assistantAppKeys, setAssistantAppKeys] = useState(null);

  // New states for chat history
  const [chatSessions, setChatSessions] = useState([]);
  const [selectedChatSession, setSelectedChatSession] = useState(null);
  const [showChatHistory, setShowChatHistory] = useState(true);
  const [isSidebarAnimating, setIsSidebarAnimating] = useState(false);
  const [isImprovingPrompt, setIsImprovingPrompt] = useState(false);
  const [selected, setSelected] = React.useState(0);
  const dropdownRef = useRef(null);
  const loadChatSessionLockRef = useRef(false);
  const hasLoadedChatSessionsRef = useRef(false);
  const hasLoadedAssistantsRef = useRef(false);

  // New states for mentions
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionSearchTerm, setMentionSearchTerm] = useState('');
  const [mentionStartPosition, setMentionStartPosition] = useState(-1);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
    hideSidebar();
  }, [newMessage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const handleSelect = e => {
    setSelected(e.selected);
  };

  const handleImprovePrompt = async () => {
    const currentMessage = newMessage.trim();
    if (!currentMessage) {
      GenerateNotification(
        buildNotification({
          title: 'Empty Prompt',
          description: 'Please enter a prompt to improve.',
          style: 'warning'
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
      return;
    }
    setIsImprovingPrompt(true);
    try {
      // Get configuration
      let response;
      try {
        response = await getConfigurationByType('system_prompt');
      } catch (error) {
        console.error('Error fetching prompt configuration:', error);
        throw new Error('Failed to load prompt configuration');
      }

      // Find the appropriate configuration
      const config = response.data.find(
        conf => conf.key === (selectedAssistant?.type || 'Default')
      );

      if (!config) {
        throw new Error('No suitable prompt configuration found');
      }

      // Improve the prompt
      const improved = await improvePrompt(
        currentMessage,
        undefined,
        config.value
      );

      if (!improved || improved.trim() === '') {
        throw new Error('Received empty improved prompt');
      }

      // Update the message
      setNewMessage(improved);

      // Success notification
      GenerateNotification(
        buildNotification({
          title: 'Prompt improved!',
          description: 'Your prompt has been enhanced for better results.',
          style: 'success'
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
    } catch (error) {
      console.error('Error improving prompt:', error);

      // Error notification
      GenerateNotification(
        buildNotification({
          title: 'Prompt Improvement Failed',
          description:
            error.message || 'An error occurred while improving your prompt',
          style: 'error'
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
    } finally {
      setIsImprovingPrompt(false);
    }
  };

  /**
   * Resets the chat to its initial state.
   */
  const handleResetChat = () => {
    setIsLoader(false);
    setSending(false);
    setSelectedAssistant(
      assistantId
        ? { id: assistantId, name: assistantName }
        : { name: 'Please Select Assistant', id: '' }
    );
    setThreadId('');
    setMessages([]);
    setNewMessage('');
    setAssistantSessions({});
    setSessionKey(null);
    setShowPromptOverlay(false);
    setIsStreamingResponse(false);
    setAttachments([]);
    setAttachmentDetails({});
    setAssistantSessions({});
    setSelectedChatSession(null);
    setBYLoginSession(false);
    // Close sidebar on mobile when resetting chat
    if (window.innerWidth <= 768) {
      setShowChatHistory(false);
    }
  };

  const hideSidebar = () => {
    const drawerWrapper = document.querySelector('.drawer-wrapper');
    const menuButton = document.querySelector(
      '.k-button-icon.k-font-icon.k-i-menu'
    );
    if (
      drawerWrapper &&
      menuButton &&
      drawerWrapper.classList.contains('drawer-active')
    ) {
      menuButton.click();
    }
  };

  useEffect(() => {
    handleResetChat();
    hideSidebar();
  }, []);


  // Keep streaming flag in sync with assistant type
  useEffect(() => {
    if (
      selectedAssistant?.type === ASSISTANT_TYPES.OPENAI ||
      selectedAssistant?.type === ASSISTANT_TYPES.SMARTAI
    ) {
      setIsStreamingResponse(true);
    }
  }, [selectedAssistant]);

  // Fetch chat sessions once on initial mount (StrictMode-safe)
  useEffect(() => {
    if (hasLoadedChatSessionsRef.current) return;
    hasLoadedChatSessionsRef.current = true;
    fetchChatSessions();
  }, []);

  useEffect(() => {
    if (assistantId) {
      setSelectedAssistant({ id: assistantId, name: assistantName });
    } else {
      if (hasLoadedAssistantsRef.current) return; // StrictMode-safe: load assistants once
      hasLoadedAssistantsRef.current = true;
      fetchAssistants();
    }
  }, [assistantId, assistantName]);

  useEffect(() => {
    const shouldHandleSession =
      messages.length > 0 &&
      !isStreamingInProgress &&
      selectedAssistant &&
      !sending;

    if (!shouldHandleSession) return;

    const handleSession = async () => {
      try {
        if (!selectedChatSession) {
          await createNewChatSession();
        } else {
          await updateCurrentChatSession();
        }
      } catch (error) {
        console.error('Failed to handle chat session:', error);
      }
    };
    handleSession();
  }, [messages, isStreamingInProgress, sending]);

  useEffect(() => {
    // Check if the selected assistant is of 'other' type and session key is not set
    if (selectedAssistant?.type !== ASSISTANT_TYPES.OPENAI && !sessionKey) {
      generateSessionKey();
    }
  }, [selectedAssistant, sessionKey]);

  // Persist session key for external consumers (e.g., tiles ApiKey)
  useEffect(() => {
    try {
      if (sessionKey) {
        sessionStorage.setItem('SMARTCHAT_SESSION_KEY', sessionKey);
      }
    } catch (_) {}
  }, [sessionKey]);

  // Toggle Chat History
  const toggleChatHistory = () => {
    setIsSidebarAnimating(true);
    setShowChatHistory(!showChatHistory);
    // Only hide sidebar on mobile
    if (window.innerWidth <= 768) {
      hideSidebar();
    }
    setTimeout(() => setIsSidebarAnimating(false), 300);
  };

  // Fetch chat sessions from API
  const fetchChatSessions = async () => {
    try {
      setIsLoadingSessions(true); // Start loading
      const response = await listChatSessions();
      if (response?.isSuccess) {
        setChatSessions(response.data);
      }
      return response.data;
    } catch (error) {
      GenerateNotification(
        buildNotification({
          title: 'Error fetching chat sessions',
          description: error.message,
          style: 'error'
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
    }finally {
      setIsLoadingSessions(false); // Stop loading
    }
  };

  // Derive unique SmartApps AppKeys (sa_tenant_key) from a list of assistants
  const computeAssistantAppKeys = assistantsList => {
    try {
      const keySet = new Set();
      (assistantsList || []).forEach(a => {
        if (
          a.type === ASSISTANT_TYPES.BLUEYONDER ||
          a.type === ASSISTANT_TYPES.SMARTAPPS ||
          a.type === ASSISTANT_TYPES.ENTERPRISE
        ) {
          try {
            const parsed = a.data ? JSON.parse(a.data) : null;
            const k = parsed?.sa_tenant_key;
            if (k) keySet.add(k);
          } catch (_) {
            // ignore malformed assistant data
          }
        }
      });
      return Array.from(keySet);
    } catch (_) {
      return [];
    }
  };

  // Create a new chat session with metadata
  const createNewChatSession = async () => {
    try {
      // Create metadata object to store assistant session data
      const metadata = {
        assistantSessions
      };

      const sessionData = createFormData({
        assistant_id: selectedAssistant.id,
        title: `${selectedAssistant.name}`,
        assistant_type: selectedAssistant.type,
        ...(selectedAssistant.type === ASSISTANT_TYPES.OPENAI && {
          thread_id: threadId
        }),
        ...(selectedAssistant.type !== ASSISTANT_TYPES.OPENAI && {
          session_key: sessionKey
        }),
        messages: JSON.stringify(
          messages.filter(
            msg => !msg.assistantId || msg.assistantId === selectedAssistant.id
          )
        ),
        metadata: JSON.stringify(metadata) // Add metadata to the session
      });

      const response = await createChatSession(sessionData);
      if (response?.isSuccess) {
        loadChatSession(response.data);
        await fetchChatSessions();
      }
    } catch (error) {
      console.error('Error creating chat session:', error);
    }
  };

  // Update current chat session with metadata
  const updateCurrentChatSession = async () => {
    try {
      // Create updated metadata object
      const metadata = {
        assistantSessions: {
          ...assistantSessions
        }
      };

      const sessionData = createFormData({
        messages: JSON.stringify(messages),
        metadata: JSON.stringify(metadata) // Add updated metadata
      });

      await updateChatSession(sessionData, selectedChatSession.id);

      // Update the local chat sessions list to keep it in sync
      setChatSessions(prevSessions =>
        prevSessions.map(session =>
          session.id === selectedChatSession.id
            ? { ...session, messages: JSON.stringify(messages) }
            : session
        )
      );
    } catch (error) {
      console.error('Error updating chat session:', error);
    }
  };

  // Load a chat session with metadata (debounced/locked to prevent double execution)
  async function loadChatSession(session) {
    if (loadChatSessionLockRef.current) return;
    loadChatSessionLockRef.current = true;
    try {
      setIsLoader(true);
      setAssistantSessions({});
      // Reset current chat state
      setThreadId('');
      setBYLoginSession(false);

      // Find the matching assistant or create a placeholder
      const matchingAssistant = assistants.find(
        assistant => parseInt(assistant.id) === parseInt(session.assistant_id)
      );

      // Only update selected assistant if it's actually different to avoid re-fetching sessions
      if (
        matchingAssistant &&
        (!selectedAssistant ||
          parseInt(selectedAssistant.id) !== parseInt(session.assistant_id))
      ) {
        setSelectedAssistant(matchingAssistant);
      }

      setSelectedChatSession(session);

      // Normalize API field naming (supports both camelCase and snake_case
      // so we always restore the exact same session key/thread id that was
      // originally persisted when the chat was created).
      const sessionThreadId = session.threadId || session.thread_id;
      const sessionKeyFromApi = session.sessionKey || session.session_key;
      const byLoginFromApi =
        session.BYLoginSession ?? session.by_login_session ?? false;

      // Load metadata if available
      if (session.metadata) {
        try {
          const parsedMetadata = JSON.parse(session.metadata);
          if (parsedMetadata) {
            setAssistantSessions(parsedMetadata.assistantSessions);
          }
        } catch (e) {
          console.error('Error parsing session metadata:', e);
        }
      }

      // Set threadId or sessionKey based on assistant type
      if (
        session.assistant_type === ASSISTANT_TYPES.OPENAI &&
        sessionThreadId
      ) {
        setThreadId(sessionThreadId);
      } else if (sessionKeyFromApi) {
        // For non-OpenAI assistants, always restore the original
        // session key that was persisted when the chat started so
        // all subsequent actions (including Add-to-Dashboard) reuse it.
        setSessionKey(sessionKeyFromApi);
      }

      // Set BYLoginSession if applicable
      if (session.assistant_type === ASSISTANT_TYPES.BLUEYONDER) {
        setBYLoginSession(!!byLoginFromApi);
      }

      // Ensure assistantSessions also has a record for this assistant with the
      // stored session key and thread/login info. This makes sure features like
      // Add-to-Dashboard and per-assistant flows can immediately reuse the
      // original session key when you reopen an old chat, instead of
      // regenerating a new one.
      if (session.assistant_id && sessionKeyFromApi) {
        const assistantIdForSession = session.assistant_id;
        setAssistantSessions(prev => {
          const current = prev || {};
          const existing = current[assistantIdForSession] || {};

          // If metadata already contained the same key, keep it as-is.
          if (existing.sessionKey && existing.sessionKey === sessionKeyFromApi) {
            return current;
          }

          return {
            ...current,
            [assistantIdForSession]: {
              ...existing,
              sessionKey: existing.sessionKey || sessionKeyFromApi,
              ...(session.assistant_type === ASSISTANT_TYPES.OPENAI &&
              sessionThreadId
                ? { threadId: existing.threadId || sessionThreadId }
                : {}),
              ...(session.assistant_type === ASSISTANT_TYPES.BLUEYONDER
                ? {
                    BYLoginSession:
                      existing.BYLoginSession ?? !!byLoginFromApi,
                  }
                : {}),
            },
          };
        });
      }

      // Fetch fresh messages (awaited to avoid double load)
      try {
        const response = await fetchChatSession(session.id);
        if (response && response.messages) {
          setMessages(response.messages);
        } else {
          // Fallback to session messages if fetch fails
          setMessages(JSON.parse(session.messages));
        }
      } catch (error) {
        console.error('Error fetching session messages:', error);
        setMessages(JSON.parse(session.messages));
      } finally {
        setIsLoader(false);
      }
    } catch (error) {
      console.error('Error loading chat session:', error);
    } finally {
      loadChatSessionLockRef.current = false;
    }
  }

  // Delete a chat session
  const deleteChatSessionHandler = async sessionId => {
    try {
      setIsLoader(true);
      const response = await deleteChatSession(sessionId);
      if (response.isSuccess) {
        if (selectedChatSession?.id === sessionId) {
          handleResetChat();
        }
        const chatSession = await fetchChatSessions();
        if (chatSession?.length === 0) {
          handleResetChat();
        }
      }
    } catch (error) {
      console.error('Error deleting chat session:', error);
    } finally {
      setIsLoader(false);
    }
  };

  const handleStreamingResponseCheck = () => {
    setIsStreamingResponse(!isStreamingResponse);
  };

  /**
   * Fetches the list of assistants.
   */
  const fetchAssistants = async () => {
    setIsLoader(true);
    try {
      const response = await listAssistants(-1);
      if (response.isSuccess) {
        // Filter out assistants with Admin Types
        const filteredAssistants = response.data.filter(
          assistant => !SPECIAL_ASSISTANT_ADMIN_TYPES.includes(assistant.type)
        );
        setAssistants(filteredAssistants);
        // Precompute SmartApps AppKeys for dashboard usage
        setAssistantAppKeys(computeAssistantAppKeys(filteredAssistants));
        // Set the first non-Custom assistant as the selected assistant
        if (filteredAssistants.length > 1) {
          setSelectedAssistant({ name: 'Please Select Assistant', id: '' }); // No assistants available
        } else {
          setSelectedAssistant(filteredAssistants[0]);
        }
      } else {
        GenerateNotification(
          buildNotification({
            title: 'Error fetching assistants',
            description: '',
            style: 'error'
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
      }
    } catch (error) {
      GenerateNotification(
        buildNotification({
          title: 'Error fetching assistants: ' + error,
          description: '',
          style: 'error'
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
    } finally {
      setIsLoader(false);
    }
  };

  /**
   * Handles selection of an assistant.
   * @param {Object} assistant - The selected assistant.
   */
  const handleAssistantSelect = assistant => {
    handleResetChat();
    setSelectedAssistant(assistant);
    setDropdownOpen(false);
    if (assistant.type === ASSISTANT_TYPES.OPENAI) {
      fetchPrompts(assistant);
    }
  };

  /**
   * Creates a new run for the thread.
   */
  const createNewRun = async (threadId, assistantId) => {
    try {
      const { id } = await createRun(threadId, assistantId);
      await waitForCompletion(threadId, id);
    } catch (error) {
      GenerateNotification(
        buildNotification({
          title: 'Error creating new run: ' + error,
          description: '',
          style: 'error'
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
    }
  };

  /**
   * Waits for the thread run to complete.
   */
  const waitForCompletion = async (threadId, runId) => {
    while (true) {
      const run = await retrieveRun(threadId, runId);
      if (run.status === 'completed') {
        setIsLoader(false);
        setSending(false);
        await fetchThreadMessages();
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  };

  /**
   * Handles sending of a message.
   */
  const handleAssistantNotSelected = (assistant = selectedAssistant) => {
    if (!assistant || assistant?.id === '') {
      setAssistantError(true);
      return true;
    }
    setAssistantError(false);
    return false;
  };

  /**
   * Find the assistant by name from the mentions
   */
  const findAssistantByName = name => {
    return assistants.find(assistant => {
      const assistantName = assistant.name.replace(/\s+/g, '').toLowerCase();
      const targetName = name.replace(/\s+/g, '').toLowerCase();
      return assistantName === targetName;
    });
  };

  /**
   * Handles the message flow for OpenAI assistants.
   * @param {string} [prompt=''] - Prompt for the message.
   * @param targetAssistant
   */
  const handleOpenAIMessageFlow = async (prompt = '', targetAssistant) => {
    const assistantToUse = targetAssistant ?? selectedAssistant;
    if (handleAssistantNotSelected(assistantToUse)) return;
    const trimmedNewMessage = newMessage.trim();
    const trimmedPrompt = Array.isArray(prompt) ? prompt[0] : prompt.trim();

    if (trimmedNewMessage.length === 0 && trimmedPrompt.length === 0) return;

    try {
      setShowPromptOverlay(false);

      // Check if we have an existing session for this assistant
      const assistantSession = assistantSessions[assistantToUse.id] || {};

      const currentMessages = [...messages];
      const messageText = trimmedNewMessage ? trimmedNewMessage : trimmedPrompt;

      setMessages([
        ...currentMessages,
        {
          role: 'user',
          text: messageText,
          timestamp: new Date(),
          assistantId: assistantToUse.id
        }
      ]);
      setNewMessage('');
      const threadIdToUse = assistantSession?.threadId;
      if (!threadIdToUse) {
        setSending(true);
        const { id } = await createThread(
          'user',
          stripAssistantMentions(messageText).trim()
        );
        // Store the new thread ID for this assistant
        setAssistantSessions(prev => ({
          ...prev,
          [assistantToUse.id]: {
            ...prev[assistantToUse.id],
            threadId: id
          }
        }));
        setThreadId(id);
        if (isStreamingResponse) {
          await startOpenAIStream(id, assistantToUse.assistant_id);
        } else {
          await createNewRun(id, assistantToUse.assistant_id);
          await fetchThreadMessages(id);
        }
      } else {
        setSending(true);
        await sendMessage(
          threadIdToUse,
          stripAssistantMentions(newMessage).trim(),
          {},
          'user',
          attachments
        );
        if (isStreamingResponse) {
          await startOpenAIStream(threadIdToUse, assistantToUse.assistant_id);
        } else {
          await createNewRun(threadIdToUse, assistantToUse.assistant_id);
          await fetchThreadMessages(threadIdToUse);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    } finally {
      setSending(false);
    }
  };
  /**
   * Handles Blue Yonder Assistant Flow
   */
  const handleBlueYonderAssistantMessageFlow = async targetAssistant => {
    const assistantToUse = targetAssistant || selectedAssistant;
    if (handleAssistantNotSelected(assistantToUse)) return;

    const trimmedNewMessage = stripAssistantMentions(newMessage.trim()).trim();
    if (trimmedNewMessage.length === 0) return;

    // Get or create session data for this assistant
    const assistantSession = assistantSessions[assistantToUse.id] || {};
    let sessionKeyToUse = assistantSession.sessionKey;
    const BYLoginState = assistantSession.BYLoginSession || false;

    if (!sessionKeyToUse) {
      sessionKeyToUse = generateSessionKeyFunction();
      setAssistantSessions(prev => ({
        ...prev,
        [assistantToUse.id]: {
          ...prev[assistantToUse.id],
          sessionKey: sessionKeyToUse,
          BYLoginSession: false
        }
      }));
    }

    // Use the session key for this specific assistant
    setSessionKey(sessionKeyToUse);

    setMessages(prevMessages => [
      ...prevMessages,
      {
        role: 'user',
        text: newMessage,
        timestamp: new Date(),
        assistantId: assistantToUse.id
      }
    ]);

    setNewMessage('');
    setSending(true);

    try {
      // Check login state for this specific assistant
      if (BYLoginState === false) {
        const BYLoginResponse = await BYLogin({
          ...JSON.parse(assistantToUse?.data),
          session_key: sessionKeyToUse
        });

        if (BYLoginResponse && BYLoginResponse?.status_code === 200) {
          // Update login state for this assistant
          setAssistantSessions(prev => ({
            ...prev,
            [assistantToUse.id]: {
              ...prev[assistantToUse.id],
              BYLoginSession: true
            }
          }));
          // Also update the main BYLoginSession state
          setBYLoginSession(true);
        } else if (BYLoginResponse?.error) {
          setMessages(prevMessages => [
            ...prevMessages,
            {
              role: 'assistant',
              text: 'There is some Error occurred, Please try again!',
              type: 'error',
              timestamp: new Date()
            }
          ]);
          return;
        } else {
          setMessages(prevMessages => [
            ...prevMessages,
            {
              role: 'assistant',
              text: BYLoginResponse?.message,
              type: BYLoginResponse?.type,
              timestamp: new Date()
            }
          ]);
          setSending(false);
          return;
        }
      }

      const assistantConfig = (() => {
        try { return JSON.parse(assistantToUse?.data || '{}'); } catch { return {}; }
      })();
      const data = {
        command: trimmedNewMessage,
        session_key: sessionKeyToUse,
        ...(assistantToUse?.type === ASSISTANT_TYPES.BLUEYONDER ||
        assistantToUse?.type === ASSISTANT_TYPES.ENTERPRISE
          ? { expose: assistantConfig?.expose || 'none' }
          : {})
      };

      let response = await sendBYMessage(data);

      // If the backend indicates that the BY session has expired (e.g. when
      // reopening an old chat and sending a new question), transparently
      // re-login with the existing session key and retry the command once.
      if (
        (response?.status_code === 401 || response?.payload === 401) &&
        typeof response?.message === 'string' &&
        response.message.toLowerCase().includes('please login')
      ) {
        try {
          const loginPayload = {
            ...assistantConfig,
            session_key: sessionKeyToUse,
          };

          const reLoginResponse = await BYLogin(loginPayload);

          if (reLoginResponse && reLoginResponse?.status_code === 200) {
            // Mark this assistant as logged-in again for this session
            setAssistantSessions(prev => ({
              ...prev,
              [assistantToUse.id]: {
                ...(prev[assistantToUse.id] || {}),
                sessionKey: sessionKeyToUse,
                BYLoginSession: true,
              },
            }));
            setBYLoginSession(true);

            // Retry the original message once after successful re-login
            response = await sendBYMessage(data);
          }
        } catch (reLoginError) {
          // If re-login fails, fall back to the original 401 response
          // so the user still sees the backend message.
          console.error('Re-login attempt failed:', reLoginError);
        }
      }

      setSending(false);

      if (response && response?.status_code === 200) {
        // Extract from canonical (top-level) fields only
        const extractedAnalysisCode = response?.analysis_code ?? null;
        const extractedAnalysisId = response?.analysis_id ?? null;

        const normalizedToolInfo = response?.tool_info
          ? { ...response.tool_info }
          : undefined;
        if (normalizedToolInfo) {
          if (extractedAnalysisCode && !normalizedToolInfo.analysis_code) {
            normalizedToolInfo.analysis_code = extractedAnalysisCode;
          }
          if (extractedAnalysisId && !normalizedToolInfo.analysis_id) {
            normalizedToolInfo.analysis_id = extractedAnalysisId;
          }
        }

        setMessages(prevMessages => [
          ...prevMessages,
          {
            role: 'assistant',
            text: response?.message,
            type: response?.type,
            data: response?.payload,
            attachment: response?.attachemnt,
            command: response?.command,
            mode: response?.mode,
            status_code: response?.status_code,
            toolInfo: normalizedToolInfo,
            analysis_code: extractedAnalysisCode,
            analysis_id: extractedAnalysisId,
            assistantId: assistantToUse.id,
            timestamp: new Date()
          }
        ]);
      } else {
        setMessages(prevMessages => [
          ...prevMessages,
          {
            role: 'assistant',
            text: response?.message,
            type: response?.type,
            timestamp: new Date()
          }
        ]);
      }
    } catch (error) {
      setMessages(prevMessages => [
        ...prevMessages,
        {
          role: 'assistant',
          text: 'There is some Error occurred, Please try again!',
          type: 'error',
          timestamp: new Date()
        }
      ]);
    } finally {
      setSending(false);
    }
  };

  // Handler to add a message (Grid/Chart) to dashboard with legacy re-exec fallback
  const handleAddToDashboardClick = async (message, messageIndex) => {
    try {
      // If toolInfo exists, open modal directly
      if (message?.toolInfo) {
        setSelectedTileMessage({ message, index: messageIndex });
        setShowAddToDashboard(true);
        return;
      }

      // Legacy backfill: attempt re-execution for BY/Enterprise messages
      // Determine assistant tied to the message
      const assistantForMessage = assistants.find(a => `${a.id}` === `${message?.assistantId || selectedAssistant?.id}`) || selectedAssistant;

      // Only attempt re-exec for BlueYonder / Enterprise
      if (
        assistantForMessage?.type === ASSISTANT_TYPES.BLUEYONDER ||
        assistantForMessage?.type === ASSISTANT_TYPES.ENTERPRISE
      ) {
        // Find a usable command: prefer message.command, else previous user text
        let commandToRun = message?.command;
        if (!commandToRun) {
          for (let i = messageIndex - 1; i >= 0; i--) {
            if (messages[i]?.role === 'user' && messages[i]?.text) {
              commandToRun = stripAssistantMentions(messages[i].text).trim();
              break;
            }
          }
        }

        if (!commandToRun) {
          GenerateNotification(
            buildNotification({
              title: 'Missing context',
              description: 'Unable to recover the original command for this message.',
              style: 'warning'
            }),
            NOTIFICATION_TYPES.APP,
            EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
          );
          return;
        }

        // Resolve sessionKey for this assistant
        const asstSession = assistantSessions[assistantForMessage.id] || {};
        let sessionKeyToUse = asstSession.sessionKey || sessionKey;
        if (!sessionKeyToUse) {
          sessionKeyToUse = generateSessionKeyFunction();
          setAssistantSessions(prev => ({
            ...prev,
            [assistantForMessage.id]: {
              ...prev[assistantForMessage.id],
              sessionKey: sessionKeyToUse
            }
          }));
        }

        const assistantConfig = (() => {
          try { return JSON.parse(assistantForMessage?.data || '{}'); } catch { return {}; }
        })();

        const reexecData = {
          command: commandToRun,
          session_key: sessionKeyToUse,
          ...(assistantForMessage?.type === ASSISTANT_TYPES.BLUEYONDER || assistantForMessage?.type === ASSISTANT_TYPES.ENTERPRISE
            ? { expose: assistantConfig?.expose || 'none' }
            : {})
        };

        const reexecResponse = await sendBYMessage(reexecData);
        if (reexecResponse?.status_code === 200) {
          // Patch the message in state with toolInfo and metadata
          setMessages(prev => {
            const updated = [...prev];
            const m = { ...updated[messageIndex] };
            m.command = reexecResponse?.command || m.command;
            m.mode = reexecResponse?.mode || m.mode;
            m.status_code = reexecResponse?.status_code || m.status_code;
            m.toolInfo = reexecResponse?.tool_info || m.toolInfo;
            const ana = reexecResponse?.analysis_code ?? null;
            if (ana) m.analysis_code = ana;
            if (m.toolInfo && ana && !m.toolInfo.analysis_code) {
              m.toolInfo.analysis_code = ana;
            }
            // Backfill analysis_id for Chart responses
            const anaId = reexecResponse?.analysis_id ?? null;
            if (anaId) m.analysis_id = anaId;
            if (m.toolInfo) {
              if (anaId && !m.toolInfo.analysis_id) m.toolInfo.analysis_id = anaId;
            }
            updated[messageIndex] = m;
            return updated;
          });

          // Persist session update if available
          if (selectedChatSession?.id) {
            await updateCurrentChatSession();
          }

          setSelectedTileMessage({
            message: {
              ...message,
              toolInfo: reexecResponse?.tool_info,
              command: reexecResponse?.command,
              mode: reexecResponse?.mode,
              status_code: reexecResponse?.status_code,
              analysis_code:
                reexecResponse?.analysis_code ||
                message?.analysis_code || null
              ,
              analysis_id:
                reexecResponse?.analysis_id ||
                message?.analysis_id || null,
            },
            index: messageIndex
          });
          setShowAddToDashboard(true);
          return;
        } else {
          GenerateNotification(
            buildNotification({
              title: 'Could not retrieve metadata',
              description: reexecResponse?.message || 'Re-execution did not return required metadata.',
              style: 'warning'
            }),
            NOTIFICATION_TYPES.APP,
            EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
          );
          return;
        }
      }

      // Non-BY message without toolInfo: show modal but user may need Advanced fields
      setSelectedTileMessage({ message, index: messageIndex });
      setShowAddToDashboard(true);
    } catch (e) {
      console.error('Error preparing Add to Dashboard:', e);
      GenerateNotification(
        buildNotification({ title: 'Error', description: e.message, style: 'error' }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
    }
  };

  /**
   * Handles Smart AI Assistant Flow
   */
  const handleSmartAIAssistantMessageFlow = async targetAssistant => {
    const assistantToUse = targetAssistant || selectedAssistant;
    if (handleAssistantNotSelected(assistantToUse)) return;

    const trimmedNewMessage = stripAssistantMentions(newMessage.trim()).trim();
    if (trimmedNewMessage.length === 0) return;

    // Get or create session key for this assistant
    const assistantSession = assistantSessions[assistantToUse.id] || {};
    let sessionKeyToUse = assistantSession.sessionKey;

    if (!sessionKeyToUse) {
      sessionKeyToUse = generateSessionKeyFunction();
      setAssistantSessions(prev => ({
        ...prev,
        [assistantToUse.id]: {
          ...prev[assistantToUse.id],
          sessionKey: sessionKeyToUse
        }
      }));
    }

    // Use the session key for this specific assistant
    setSessionKey(sessionKeyToUse);

    // Rest of the function remains the same...
    setMessages(prevMessages => [
      ...prevMessages,
      {
        role: 'user',
        text: newMessage,
        timestamp: new Date(),
        assistantId: assistantToUse.id
      }
    ]);

    setNewMessage('');
    setSending(true);

    const data = {
      question: trimmedNewMessage,
      session_key: sessionKeyToUse,
      stream: isStreamingResponse
    };

    try {
      if (isStreamingResponse) {
        setIsStreamingInProgress(true);
        await handleSmartRagStream(
          JSON.parse(assistantToUse.data).guid,
          data,
          setMessages
        );
        setIsStreamingInProgress(false);
      } else {
        const response = await sendQuestion(
          JSON.parse(assistantToUse.data).guid,
          data
        );
        if (response?.isSuccess) {
          setMessages(prevMessages => [
            ...prevMessages,
            {
              role: 'assistant',
              text: response.data.answer,
              timestamp: new Date()
            }
          ]);
        } else {
          setMessages(prevMessages => [
            ...prevMessages,
            {
              role: 'assistant',
              text: 'There is some Error occurred, Please try again!',
              type: 'error',
              timestamp: new Date()
            }
          ]);
        }
      }
    } catch (error) {
      setMessages(prevMessages => [
        ...prevMessages,
        {
          role: 'assistant',
          text: 'There is some Error occurred, Please try again!',
          type: 'error',
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsStreamingInProgress(false);
      setSending(false);
    }
  };

  /**
   * Start Stream
   * @param threadId
   * @param assistantId
   */
  const startOpenAIStream = async (threadId, assistantId) => {
    try {
      setIsStreamingInProgress(true);
      // Create a run with stream option set to true
      const runResponse = await createRun(threadId, assistantId, true);

      // Call the handleStream function with the runResponse
      await handleOpenAIStream(runResponse, setMessages);
    } catch (error) {
      console.error('Error starting stream:', error);
    } finally {
      setIsStreamingInProgress(false);
    }
  };

  /**
   * Handles message sending when the Enter key is pressed.
   * @param prompt
   */
  const handleMessageSend = async (prompt = '') => {
    if (handleAssistantNotSelected()) return;

    // Preserve current messages before any changes
    const currentMessages = [...messages];

    // Check for mentions in the message
    const mentions = parseMentions(newMessage || prompt);

    try {
      // If there's a mention, determine which assistant to use
      if (mentions.length > 0) {
        const mentionedAssistant = findAssistantByName(mentions[0]);
        if (mentionedAssistant) {
          // Route to the appropriate handler
          let result;
          if (mentionedAssistant.type === ASSISTANT_TYPES.OPENAI) {
            result = await handleOpenAIMessageFlow(prompt, mentionedAssistant);
          } else if (mentionedAssistant.type === ASSISTANT_TYPES.SMARTAI) {
            result =
              await handleSmartAIAssistantMessageFlow(mentionedAssistant);
          } else if (
            mentionedAssistant.type === ASSISTANT_TYPES.BLUEYONDER ||
            mentionedAssistant.type === ASSISTANT_TYPES.SMARTAPPS
          ) {
            result =
              await handleBlueYonderAssistantMessageFlow(mentionedAssistant);
          }
          // Restore the previous assistant
          return result;
        }
      }
      // Original logic for selected assistant
      if (selectedAssistant?.type === ASSISTANT_TYPES.OPENAI) {
        return await handleOpenAIMessageFlow(prompt);
      } else if (selectedAssistant?.type === ASSISTANT_TYPES.SMARTAI) {
        return await handleSmartAIAssistantMessageFlow();
      } else if (
        selectedAssistant?.type === ASSISTANT_TYPES.BLUEYONDER ||
        selectedAssistant?.type === ASSISTANT_TYPES.SMARTAPPS ||
        selectedAssistant?.type === ASSISTANT_TYPES.ENTERPRISE
      ) {
        return await handleBlueYonderAssistantMessageFlow();
      }
    } catch (error) {
      setMessages(currentMessages);
      throw error;
    }
  };

  /**
   * Fetches the thread messages list.
   * @param {string} [id] - ID of the thread.
   */
  const fetchThreadMessages = async id => {
    try {
      if (id) {
        const threadMessages = await listMessages(id);
        if (threadMessages.length > 0) {
          // Get the first message and append it to existing messages
          setMessages(prevMessages => [...prevMessages, threadMessages[0]]);
        }
      }
    } catch (error) {
      // Handle error
    }
  };

  /**
   * Handles text input changes and checks for @ mentions
   */
  const handleTextareaChange = e => {
    const newValue = e.target.value;
    setNewMessage(newValue);

    // Check if we should show the mention dropdown
    if (textareaRef.current) {
      const cursorPos = getCursorPosition(textareaRef.current);
      const mentionStart = findMentionStart(newValue, cursorPos);

      if (mentionStart !== -1) {
        const searchTerm = getMentionSearchTerm(
          newValue,
          mentionStart,
          cursorPos
        );
        setMentionSearchTerm(searchTerm);
        setMentionStartPosition(mentionStart);

        // Calculate position for the dropdown
        const textareaRect = textareaRef.current.getBoundingClientRect();
        const textBeforeCursor = newValue.substring(0, mentionStart);
        const lines = textBeforeCursor.split('\n');
        const lineCount = lines.length;
        const lastLineLength = lines[lines.length - 1].length;

        // Approximate position calculation
        const lineHeight = 20; // Approximate line height in pixels
        const charWidth = 8; // Approximate character width in pixels

        const top = textareaRect.top + (lineCount - 1) * lineHeight + 25;
        const left = textareaRect.left + lastLineLength * charWidth;

        setMentionPosition({ top, left });
        setShowMentionDropdown(true);
      } else {
        setShowMentionDropdown(false);
      }
    }
  };

  useEffect(() => {
    // This effect ensures our state remains consistent when assistant changes
    if (selectedAssistant && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (
        lastMessage.assistantId &&
        lastMessage.assistantId !== selectedAssistant.id
      ) {
        // If the last message was from a different assistant, ensure we maintain context
        setMessages(prev => [
          ...prev.slice(0, -1),
          {
            ...lastMessage,
            assistantId: selectedAssistant.id
          }
        ]);
      }
    }
  }, [selectedAssistant, messages]);

  /**
   * Handles selection of an assistant from the mention dropdown
   */
  const handleMentionSelect = assistant => {
    if (textareaRef.current && mentionStartPosition !== -1) {
      const cursorPos = getCursorPosition(textareaRef.current);
      const { newText, newCursorPos } = insertMention(
        newMessage,
        mentionStartPosition,
        cursorPos,
        assistant.name.replace(/\s+/g, '')
      );

      setNewMessage(newText);

      // Set cursor position after the inserted mention
      setTimeout(() => {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }

    // Check if we have a session for this assistant
    const assistantSession = assistantSessions[assistant.id] || {};

    // Generate new session key if needed (for non-OpenAI assistants)
    if (
      assistant.type !== ASSISTANT_TYPES.OPENAI &&
      !assistantSession.sessionKey
    ) {
      const newSessionKey = generateSessionKeyFunction();
      setAssistantSessions(prev => ({
        ...prev,
        [assistant.id]: {
          ...prev[assistant.id],
          sessionKey: newSessionKey,
          BYLoginSession: false // Reset login state for new session
        }
      }));
      setSessionKey(newSessionKey);
    } else if (assistantSession.sessionKey) {
      setSessionKey(assistantSession.sessionKey);
    }

    // For OpenAI assistants, set thread ID if exists
    if (
      assistant.type === ASSISTANT_TYPES.OPENAI &&
      assistantSession.threadId
    ) {
      setThreadId(assistantSession.threadId);
    }

    setShowMentionDropdown(false);
  };
  /**
   * Submits the message on key press (Enter).
   * @param {Object} event - Event object.
   */
  const handleKeyPress = event => {
    // If mention dropdown is open and arrow keys are pressed, handle dropdown navigation
    if (
      showMentionDropdown &&
      (event.key === 'ArrowUp' || event.key === 'ArrowDown')
    ) {
      event.preventDefault();
      // Navigation logic would go here if implementing keyboard navigation
      return;
    }

    // If mention dropdown is open and Tab or Enter is pressed, select the first option
    if (showMentionDropdown && (event.key === 'Tab' || event.key === 'Enter')) {
      event.preventDefault();
      const filteredAssistants = assistants.filter(assistant =>
        assistant.name.toLowerCase().includes(mentionSearchTerm.toLowerCase())
      );
      if (filteredAssistants.length > 0) {
        handleMentionSelect(filteredAssistants[0]);
      }
      return;
    }

    // Normal Enter key handling for sending messages
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      setShowPromptOverlay(false);
      setShowMentionDropdown(false);
      if (!sending) {
        handleMessageSend();
      }
    }
  };

  /**
   * Handles the selection of a prompt and hides the prompt overlay.
   * @param {string} prompt - Selected prompt.
   */
  const handlePromptSelection = prompt => {
    handleMessageSend(prompt);
    setShowPromptOverlay(false);
  };

  /**
   * Fetches prompts based on assistant instructions and updates the state.
   * @param promptsData
   */
  const fetchPrompts = async promptsData => {
    setIsLoader(true);
    if (promptsData && promptsData?.prompts) {
      const shuffledPrompts = promptsData.prompts
        .map(item => item.prompt)
        .sort(() => Math.random() - 0.5);
      const randomPrompts = shuffledPrompts.slice(0, 4);
      setPrompts(randomPrompts);
      setShowPromptOverlay(true);
    }
    setIsLoader(false);
  };

  // Update the session key effect to handle mentioned assistants
  useEffect(() => {
    // Only generate session key for non-OpenAI assistants
    if (selectedAssistant?.type !== ASSISTANT_TYPES.OPENAI) {
      // Check if we already have a session key for this assistant
      const assistantSession = assistantSessions[selectedAssistant?.id] || {};
      if (!assistantSession.sessionKey) {
        generateSessionKey(selectedAssistant?.id);
      } else {
        setSessionKey(assistantSession.sessionKey);
      }
    }
  }, [selectedAssistant]);

  /**
   * Generate or retrieve session key only when other type assistant message flow is called
   */
  // Modify the generateSessionKey function to store in assistantSessions
  const generateSessionKey = async (assistantId = selectedAssistant?.id) => {
    try {
      const key = generateSessionKeyFunction();
      if (assistantId) {
        setAssistantSessions(prev => ({
          ...prev,
          [assistantId]: {
            ...prev[assistantId],
            sessionKey: key
          }
        }));
      }
      setSessionKey(key);
    } catch (error) {
      console.error('Error generating session key:', error);
    }
  };

  /**
   * Handle File Change
   */
  const handleFileChange = async event => {
    setIsLoader(true);
    const file = event.target.files[0];
    if (file) {
      // Check file extension
      const allowedExtensions = ['pdf', 'jsonl'];
      const fileExtension = file.name.split('.').pop().toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        // Handle invalid file extension
        GenerateNotification(
          buildNotification({
            title: 'Invalid file type',
            description: `Only .pdf and .jsonl files are allowed.`,
            style: 'error'
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
        setIsLoader(false);
        return;
      }
      try {
        const response = await uploadFile(file);
        setAttachments([
          { file_id: response.id, tools: [{ type: 'file_search' }] }
        ]);
        setAttachmentDetails([{ file_id: response.id, file_name: file.name }]);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
    setIsLoader(false);
  };

  /**
   * Handle File Delete
   */
  const handleFileDelete = async fileId => {
    setIsLoader(true);
    try {
      // Call the deleteFile function with the file ID
      await deleteFile(fileId);
      // Remove the deleted file from the attachments state
      setAttachments(
        attachments.filter(attachment => attachment.file_id !== fileId)
      );
      setAttachmentDetails(
        attachmentDetails.filter(attachment => attachment.file_id !== fileId)
      );
    } catch (error) {
      console.error('Error deleting file:', error);
    }
    setIsLoader(false);
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };
  const scrollToBottom = useCallback(() => {
    if (chatWindowRef.current) {
      // Use setTimeout to ensure this runs after the DOM updates
      setTimeout(() => {
        chatWindowRef.current.scrollTo({
          top: chatWindowRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, []);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className={'smart-chat'}>
        <div className="header smart-chat-header !k-mb-0">
          <button
            className="k-button-icon toggle-sidebar-button"
            onClick={toggleChatHistory}
            title={showChatHistory ? 'Hide History' : 'Show History'}
          >
            <span
              className={`k-icon k-font-icon ${showChatHistory ? 'k-i-chevron-left' : 'k-i-chevron-right'}`}
            />
          </button>
          <button
            className="open-dashboard-button"
            onClick={() => setShowDashboardOverlay(true)}
            title="Open Dashboard"
          >
            <span className="k-icon k-font-icon k-i-grid-layout" />
            <span className="btn-label">Dashboard</span>
          </button>
        </div>
        <div className="chat-container">
          <React.Suspense fallback={<Loader />}>
            <div className="chat-layout">
              {showChatHistory && (
                <div
                  className={`sidebar-overlay ${showChatHistory ? 'visible' : ''}`}
                  onClick={toggleChatHistory}
                />
              )}
              <div
                className={`chat-history-sidebar-container ${
                  showChatHistory ? 'visible' : 'hidden'
                } ${isSidebarAnimating ? 'animating' : ''}`}
              >
                <Card className="chat-history-sidebar">
                  <div className="sidebar-header">
                    <h6 className={'k-d-flex'}>
                      <button
                        className="k-button-icon toggle-sidebar-button"
                        onClick={toggleChatHistory}
                        title={
                          showChatHistory ? 'Hide History' : 'Show History'
                        }
                      >
                        <span
                          className={`k-icon k-font-icon ${showChatHistory ? 'k-i-chevron-left' : 'k-i-chevron-right'}`}
                        />
                      </button>
                      Chat History
                    </h6>
                    <div className="sidebar-actions">
                      <button
                        className="new-chat-btn gpt-custom-gradient"
                        onClick={handleResetChat}
                      >
                        <span className="btn-content">
                          <span className="icon-wrapper">
                            <FontAwesomeIcon icon={faPlus} />
                          </span>
                          <span className="btn-text">New</span>
                        </span>
                      </button>
                    </div>
                  </div>
                  <div className="session-list">
                    {isLoadingSessions ? (
                      <div className="mini-loader-container">
                        {/* Mini loader element */}
                        <div className='mini-loader'/>
                      </div>
                    ) : chatSessions.length === 0 ? (
                      <div className="no-sessions">No Chat History found</div>
                    ) : (
                      <TabStrip
                        selected={selected}
                        onSelect={handleSelect}
                        size={'small'}
                      >
                        <TabStripTab title="Recent">
                          <ListView
                            data={chatSessions.slice(0, 5)}
                            style={{ width: '100%' }}
                            virtual
                            item={({ dataItem }) => {
                              // Parse messages to get the first user message
                              const messages = JSON.parse(dataItem.messages);
                              const firstUserMessage = messages.find(
                                msg => msg.role === 'user'
                              );
                              const firstMessageText =
                                firstUserMessage?.text || 'New conversation';

                              return (
                                <div
                                  className={`session-item ${selectedChatSession?.id === dataItem.id ? 'active' : ''}`}
                                  onClick={e => { e.preventDefault(); e.stopPropagation(); loadChatSession(dataItem); }}
                                >
                                  <div className="session-title">
                                    {dataItem.title.substring(0, 40)}
                                    <span className="session-date">
                                      {formatSessionDate(dataItem?.created_at)}
                                    </span>
                                  </div>
                                  <div className="session-preview">
                                    {firstMessageText.length > 33
                                      ? `${firstMessageText.substring(0, 33)}...`
                                      : firstMessageText}
                                  </div>
                                  <button
                                    className="k-button-icon delete-button"
                                    onClick={e => {
                                      e.stopPropagation();
                                      deleteChatSessionHandler(dataItem.id);
                                    }}
                                    title="Delete"
                                  >
                                    <span className="k-font-icon k-i-trash" />
                                  </button>
                                </div>
                              );
                            }}
                          />
                        </TabStripTab>
                        <TabStripTab title="View All">
                          <ListView
                            data={chatSessions}
                            style={{ width: '100%' }}
                            virtual
                            item={({ dataItem }) => {
                              const messages = JSON.parse(dataItem.messages);
                              const firstUserMessage = messages.find(
                                msg => msg.role === 'user'
                              );
                              const firstMessageText =
                                firstUserMessage?.text || 'New conversation';

                              return (
                                <div
                                  className={`session-item ${selectedChatSession?.id === dataItem.id ? 'active' : ''}`}
                                  onClick={e => { e.preventDefault(); e.stopPropagation(); loadChatSession(dataItem); }}
                                >
                                  <div className="session-title">
                                    {dataItem.title.substring(0, 40)}
                                    <span className="session-date">
                                      {formatSessionDate(dataItem?.created_at)}
                                    </span>
                                  </div>
                                  <div className="session-preview">
                                    {firstMessageText.length > 33
                                      ? `${firstMessageText.substring(0, 33)}...`
                                      : firstMessageText}
                                  </div>
                                  <button
                                    className="k-button-icon delete-button"
                                    onClick={e => {
                                      e.stopPropagation();
                                      deleteChatSessionHandler(dataItem.id);
                                    }}
                                    title="Delete"
                                  >
                                    <span className="k-font-icon k-i-trash" />
                                  </button>
                                </div>
                              );
                            }}
                          />
                        </TabStripTab>
                      </TabStrip>
                    )}
                  </div>
                </Card>
              </div>

              {/* Add this new collapsed sidebar bar */}
              {!showChatHistory && (
                <div className="collapsed-sidebar-bar">
                  <button
                    className="k-button-icon toggle-sidebar-button"
                    onClick={toggleChatHistory}
                    title="Show History"
                  >
                    <span className="k-icon k-font-icon k-i-chevron-right" />
                  </button>
                  <button
                    className="new-chat-btn gpt-custom-gradient"
                    onClick={handleResetChat}
                  >
                    <span className="btn-content">
                      <span className="icon-wrapper">
                        <FontAwesomeIcon icon={faPlus} />
                      </span>
                    </span>
                  </button>
                </div>
              )}
              {/* Main Chat Area */}
              <div
                className={`main-chat-area ${showChatHistory ? 'with-sidebar' : ''}`}
                ref={mainChatAreaRef}
              >
                {/* Floating open dashboard button (visible on larger screens too) */}
                <button
                  className="open-dashboard-fab"
                  title="Open Dashboard"
                  onClick={() => setShowDashboardOverlay(true)}
                >
                  <FontAwesomeIcon icon={faTh} />
                </button>
                {/* Chat Messages */}
                <Card className="chat-box">
                  {isLoader && (
                    <div className="loader-wrapper">
                      <Loader />
                    </div>
                  )}
                  <div className="chat-window" ref={chatWindowRef}>
                    {messages.slice(0).map((message, index) => {
                      return (
                        <ChatMessage
                          key={index}
                          role={message?.role}
                          type={message?.type}
                          text={message?.text}
                          data={message?.data}
                          attachment={message?.attachment}
                          timestamp={message?.timestamp}
                          progress={message?.progress}
                          status={message?.status}
                          error={message?.error}
                          onAddToDashboard={
                            // Only show Add-to-Dashboard when we have the required metadata (toolInfo)
                            (message?.toolInfo && (message?.type === 'Grid' || message?.type === 'Chart'))
                              ? () => handleAddToDashboardClick(message, index)
                              : undefined
                          }
                        />
                      );
                    })}
                    {showPromptOverlay && (
                      <div className="prompt-cards">
                        {prompts.map((prompt, index) => (
                          <div
                            key={index}
                            className="prompt-card"
                            onClick={() => handlePromptSelection(prompt)}
                          >
                            <span>{prompt}</span>
                            <button className="delete-button k-float-right k-cursor-pointer">
                              <span className="fas fa-paper-plane" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>

                {/* New Chat Input Area - Positioned at bottom or middle based on messages */}
                <div
                  className={`chat-input-container ${messages.length > 0 ? 'bottom-position' : 'middle-position'}`}
                >
                  {messages.length === 0 && (
                    <h5 className={'k-text-center k-text-bold'}>
                      <strong>Chat with your desired assistant!</strong>
                    </h5>
                  )}
                  <div className="loader-div">
                    {sending && <div className="dots" />}
                  </div>
                  <div className="textarea-container">
                    <textarea
                      disabled={sending}
                      ref={textareaRef}
                      value={newMessage}
                      onChange={handleTextareaChange}
                      onKeyDown={handleKeyPress}
                      placeholder="Type your message... (Use @ to mention an assistant)"
                      rows={1}
                    />

                    {/* Mention dropdown */}
                    <MentionDropdown
                      assistants={assistants}
                      isVisible={showMentionDropdown}
                      position={mentionPosition}
                      onSelect={handleMentionSelect}
                      onClose={() => setShowMentionDropdown(false)}
                      searchTerm={mentionSearchTerm}
                      selectedAssistant={selectedAssistant}
                    />

                    <div className="textarea-buttons">
                      {selectedAssistant?.type === ASSISTANT_TYPES.OPENAI && (
                        <>
                          <input
                            type="file"
                            className="k-d-none"
                            onChange={handleFileChange}
                            id="file-input"
                            accept=".pdf,.jsonl"
                          />
                          <label
                            htmlFor="file-input"
                            className="file-button"
                            title="Attach file"
                          >
                            <FontAwesomeIcon icon={faPaperclip} />
                          </label>
                        </>
                      )}

                      {(selectedAssistant?.type === ASSISTANT_TYPES.OPENAI ||
                        selectedAssistant?.type ===
                          ASSISTANT_TYPES.SMARTAI) && (
                        <button
                          className={`streaming-button ${isStreamingResponse ? 'active' : ''}`}
                          onClick={handleStreamingResponseCheck}
                          title={
                            isStreamingResponse
                              ? 'Disable streaming'
                              : 'Enable streaming'
                          }
                        >
                          <FontAwesomeIcon icon={faBolt} />
                        </button>
                      )}

                      <button
                        className="magic-button"
                        onClick={handleImprovePrompt}
                        disabled={
                          !selectedAssistant?.id ||
                          !newMessage.trim() ||
                          sending ||
                          isImprovingPrompt
                        }
                        title="Improve this prompt"
                      >
                        {isImprovingPrompt ? (
                          <span className="k-icon k-i-loading" />
                        ) : (
                          <FontAwesomeIcon icon={faWandMagicSparkles} />
                        )}
                      </button>

                      <button
                        className="send-button"
                        onClick={() => handleMessageSend()}
                        disabled={sending}
                        title="Send message"
                      >
                        {sending ? (
                          <span className="k-icon k-i-loading" />
                        ) : (
                          <FontAwesomeIcon icon={faPaperPlane} />
                        )}
                      </button>

                    </div>
                  </div>

                  {attachmentDetails && attachments.length > 0 && (
                    <div className="attachments">
                      {attachmentDetails?.map((attachment, index) => (
                        <div key={index} className="attachment">
                          <p>{attachment.file_name}</p>
                          <button
                            className="delete-button"
                            onClick={() => handleFileDelete(attachment.file_id)}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {assistantId == null && (
                    <AssistantSelect
                      assistants={assistants}
                      selectedAssistant={selectedAssistant}
                      onAssistantSelect={handleAssistantSelect}
                      onResetChat={handleResetChat}
                      showError={assistantError}
                    />
                  )}
                </div>
              </div>
            </div>
          </React.Suspense>
        </div>

        {/* Add to Dashboard Modal */}
        {showAddToDashboard && (
          <AddToDashboardModal
            message={selectedTileMessage?.message}
            // For SmartApps / Enterprise assistants, pass their SmartApps AppKey (sa_tenant_key)
            assistantAppKey={(() => {
              try {
                const asstId = selectedTileMessage?.message?.assistantId || selectedAssistant?.id;
                const assistant = assistants.find(a => `${a.id}` === `${asstId}`) || selectedAssistant;
                if (!assistant?.data) return undefined;
                const parsed = JSON.parse(assistant.data || '{}');
                return parsed?.sa_tenant_key || undefined;
              } catch (_) {
                return undefined;
              }
            })()}
            onClose={() => { setShowAddToDashboard(false); setSelectedTileMessage(null); }}
            onSaved={() => {
              setShowAddToDashboard(false);
              setSelectedTileMessage(null);
              // If dashboard overlay is open, force it to refresh immediately.
              // If it’s closed, this still invalidates any prefetched snapshot by causing a real fetch on next open.
              setDashboardRefreshToken(t => t + 1);
            }}
          />)
        }

        {/* Full-screen Dashboard Overlay
            SmartChatDashboard now relies solely on the assistant/SmartApps
            server for tile storage and visibility (no Laravel mappings).
        */}
        <div
          className={`dashboard-overlay ${showDashboardOverlay ? 'visible' : 'hidden'}`}
          role="dialog"
          aria-modal="true"
          style={{ display: showDashboardOverlay ? 'block' : 'none' }}
        >
          <div className="dashboard-toolbar">
            <span className="title">Dashboard</span>
            <div className="spacer" />
            <button
              className="close-dashboard-button"
              onClick={() => setShowDashboardOverlay(false)}
              title="Close"
            >
              <span className="k-icon k-font-icon k-i-close" />
            </button>
          </div>
          <div className="dashboard-content">
            <SmartChatDashboard
              refreshToken={dashboardRefreshToken}
              appKeys={assistantAppKeys}
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
export default SmartChat;
